import type { IProductTagTheme } from "@/api/AppApi/services/company/interfaces/ICompany";
import { getVisibleProductTags } from "@/utils/functions/product-tags";

interface Props {
  /** `product.tag_ids` — the catalog tags assigned to this product. */
  tagIds: number[] | null | undefined;
  /** `theme.components.product.product_tags` — the merchant's display config. */
  themeTags: IProductTagTheme[] | null | undefined;
  /** Used to build a DOM id that is unique per product. */
  productId: number | string;
  className?: string;
}

/**
 * Product-details rendering of the theme-configured catalog tags, as inline chips beside
 * the NEW / discount badges. The product card uses the ribbon-badge form over the image
 * instead (`ProductTagBadge`), but both share the same styling hooks so one custom-CSS
 * rule reaches a tag wherever it appears:
 *
 *   #product-tag-987-12     { --product-tag-bg: #000; }   one tag on one product
 *   .product-tag-12         { --product-tag-bg: #000; }   that tag everywhere
 *   [data-tag-name="Sale"]  { --product-tag-bg: #000; }   by tag name
 *
 * Renders nothing at all when no configured tag is assigned, so no empty container.
 *
 * Deliberately hook-free and free of `"use client"` — zero client JS.
 */
const ProductTags: React.FC<Props> = ({
  tagIds,
  themeTags,
  productId,
  className,
}) => {
  const visibleTags = getVisibleProductTags(tagIds, themeTags);
  if (visibleTags.length === 0) return null;

  return (
    <ul
      id={`product-tags-${productId}`}
      className={`product-tags${className ? ` ${className}` : ""}`}
    >
      {visibleTags.map((tag) => (
        <li
          key={tag.id}
          id={`product-tag-${productId}-${tag.id}`}
          className={`product-tag product-tag-${tag.id}`}
          data-tag-id={tag.id}
          data-tag-name={tag.name}
          style={
            {
              "--product-tag-theme-bg": tag.bgColor,
              "--product-tag-theme-color": tag.textColor,
            } as React.CSSProperties
          }
        >
          {tag.name}
        </li>
      ))}
    </ul>
  );
};

export default ProductTags;
